<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import ScannerCamera from './ui/ScannerCamera.vue';
import { sanitizeQrCode } from './model/qrDetection';

const props = defineProps<{
    scannedItems: readonly string[];
}>();

const emit = defineEmits<{
    detect: [qrCode: string];
}>();

const isOpen = defineModel<boolean>('isOpen', { required: true });

const manualInput = ref('');
const manualInputError = ref<string | null>(null);
const detectedQrCode = ref<string | null>(null);
const confirmedItems = ref<string[]>([]);

const allScannedItems = computed(() => [...props.scannedItems, ...confirmedItems.value]);

const playBeepSound = () => {
    const audioContext = new AudioContext();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.type = 'sine';
    oscillator.frequency.value = 1000;
    gainNode.gain.value = 0.3;

    oscillator.start();
    oscillator.stop(audioContext.currentTime + 0.1);
};

const resetState = () => {
    manualInput.value = '';
    manualInputError.value = null;
    detectedQrCode.value = null;
    confirmedItems.value = [];
};

const closeModal = () => {
    isOpen.value = false;
};

const handleDetect = (qrCode: string) => {
    detectedQrCode.value = qrCode;
};

const handlePreviewConfirm = () => {
    if (!detectedQrCode.value) return;
    playBeepSound();
    confirmedItems.value = [...confirmedItems.value, detectedQrCode.value];
    emit('detect', detectedQrCode.value);
    detectedQrCode.value = null;
};

const handlePreviewCancel = () => {
    detectedQrCode.value = null;
};

const handleManualInputConfirm = () => {
    if (!manualInput.value.trim()) {
        manualInputError.value = 'QRコードを入力してください';
        return;
    }
    manualInputError.value = null;
    playBeepSound();
    emit('detect', sanitizeQrCode(manualInput.value));
    manualInput.value = '';
};

watch(manualInput, () => {
    manualInputError.value = null;
});

const handleComplete = () => {
    closeModal();
};
</script>

<template>
    <v-dialog v-model="isOpen" fullscreen transition="dialog-bottom-transition" @after-leave="resetState">
        <div class="modal">
            <header class="header">
                <div class="header-spacer"></div>
                <button type="button" class="close-button" aria-label="閉じる" @click="closeModal">
                    <v-icon color="white" size="28">mdi-close</v-icon>
                </button>
            </header>

            <main class="main">
                <ScannerCamera
                    v-if="isOpen"
                    :detected-qr-code="detectedQrCode"
                    :scanned-items="allScannedItems"
                    @detect="handleDetect"
                    @confirm="handlePreviewConfirm"
                    @cancel="handlePreviewCancel"
                />

                <div class="manual-input-area">
                    <div class="manual-input-wrapper">
                        <input
                            v-model="manualInput"
                            type="text"
                            inputmode="url"
                            autocapitalize="none"
                            autocomplete="off"
                            :class="['input', { 'input-error': manualInputError }]"
                            placeholder="QRコード手入力"
                            @keydown.enter="handleManualInputConfirm"
                        />
                        <p v-if="manualInputError" class="error-message">{{ manualInputError }}</p>
                    </div>
                    <button type="button" class="ok-button" @click="handleManualInputConfirm">OK</button>
                </div>
            </main>

            <footer class="footer">
                <button type="button" class="complete-button" @click="handleComplete">読み取り完了</button>
            </footer>
        </div>
    </v-dialog>
</template>

<style scoped>
.modal {
    display: flex;
    flex-direction: column;
    height: 100%;
    background-color: #000;
}

.header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background-color: #000;

    & .header-spacer {
        width: 28px;
    }

    & .close-button {
        background: none;
        border: none;
        cursor: pointer;
        padding: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
}

.main {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 16px;
    min-height: 0;

    & .manual-input-area {
        display: flex;
        gap: 8px;
        width: 100%;
        margin: 24px auto 0;
        align-items: flex-start;

        & .manual-input-wrapper {
            flex: 1;
            min-width: 0;
        }

        & .input {
            width: 100%;
            padding: 12px 16px;
            border: 1px solid #ccc;
            border-radius: 4px;
            font-size: 16px;
            background-color: #fff;

            &::placeholder {
                color: #999;
            }

            &.input-error {
                border-color: #f44336;
            }
        }

        & .error-message {
            color: #f44336;
            font-size: 12px;
            margin: 4px 0 0;
        }

        & .ok-button {
            flex-shrink: 0;
            padding: 12px 24px;
            background-color: #e0e0e0;
            border: none;
            border-radius: 4px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;

            &:hover {
                background-color: #d0d0d0;
            }
        }
    }
}

.footer {
    padding: 16px;
    background-color: #fff;

    & .complete-button {
        width: 100%;
        padding: 16px;
        background-color: #00bcd4;
        border: none;
        border-radius: 8px;
        color: #fff;
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;

        &:hover {
            background-color: #00acc1;
        }
    }
}
</style>
